// SPDX-License-Identifier: AGPL-3.0-or-later

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {http} from '@app/features/platform/transport/RestTransport';
import {Logger} from '@app/features/platform/utils/AppLogger';
import Tasks, {type Task, type TaskStatus} from '@app/features/tasks/state/Tasks';
import {TODO_ADDED_DESCRIPTOR, TODO_FAILED_DESCRIPTOR} from '@app/features/tasks/utils/TaskMessageDescriptors';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {i18n} from '@lingui/core';

const logger = new Logger('Tasks');

export interface CreateTaskInput {
	title: string;
	notes?: string | null;
	assignee_id?: string | null;
	due_date?: string | null;
	status?: TaskStatus;
	channel_id?: string | null;
	message_id?: string | null;
	private?: boolean;
}

export interface UpdateTaskInput {
	title?: string;
	notes?: string | null;
	assignee_id?: string | null;
	assignee_ids?: Array<string>;
	due_date?: string | null;
	status?: TaskStatus;
	archived?: boolean;
	private?: boolean;
}

export async function loadGuildTasks(guildId: string, {force = false}: {force?: boolean} = {}): Promise<void> {
	if (!force && (Tasks.isLoaded(guildId) || Tasks.isLoading(guildId))) return;
	Tasks.handleLoadStart(guildId);
	try {
		const response = await http.get<Array<Task>>(Endpoints.GUILD_TASKS(guildId));
		Tasks.handleLoadSuccess(guildId, response.body ?? []);
	} catch (error) {
		logger.error('Failed to load to-dos', error);
		Tasks.handleLoadError(guildId);
	}
}

export async function createTask(guildId: string, input: CreateTaskInput): Promise<Task | null> {
	try {
		const response = await http.post<Task>(Endpoints.GUILD_TASKS(guildId), {body: input});
		if (response.body) Tasks.upsert(response.body);
		return response.body ?? null;
	} catch (error) {
		logger.error('Failed to create to-do', error);
		return null;
	}
}

/** Saves locally first so a drag on the board or a tick feels instant. */
export async function updateTask(task: Task, input: UpdateTaskInput): Promise<void> {
	const {archived, ...fields} = input;
	let archivedAt = task.archived_at;
	if (archived !== undefined) archivedAt = archived ? new Date().toISOString() : null;
	const optimistic = {...task, ...fields, archived_at: archivedAt} as Task;
	if (fields.assignee_ids) optimistic.assignee_id = fields.assignee_ids[0] ?? null;
	Tasks.upsert(optimistic);
	try {
		const response = await http.patch<Task>(Endpoints.GUILD_TASK(task.guild_id, task.id), {body: input});
		if (response.body) Tasks.upsert(response.body);
	} catch (error) {
		logger.error('Failed to update to-do', error);
		Tasks.upsert(task);
	}
}

/** Posts a comment; the to-do comes back with every comment and replaces the local copy. */
export async function addTaskComment(
	task: Task,
	content: string,
	mentionIds: Array<string> = [],
): Promise<boolean> {
	try {
		const response = await http.post<Task>(Endpoints.GUILD_TASK_COMMENTS(task.guild_id, task.id), {
			body: {content, mention_ids: mentionIds},
		});
		if (response.body) Tasks.upsert(response.body);
		return true;
	} catch (error) {
		logger.error('Failed to comment on to-do', error);
		return false;
	}
}

export async function deleteTaskComment(task: Task, commentId: string): Promise<void> {
	Tasks.upsert({...task, comments: (task.comments ?? []).filter((comment) => comment.id !== commentId)});
	try {
		const response = await http.delete<Task>(Endpoints.GUILD_TASK_COMMENT(task.guild_id, task.id, commentId));
		if (response.body) Tasks.upsert(response.body);
	} catch (error) {
		logger.error('Failed to delete to-do comment', error);
		Tasks.upsert(task);
	}
}

export async function deleteTask(task: Task): Promise<void> {
	Tasks.remove(task.guild_id, task.id);
	try {
		await http.delete(Endpoints.GUILD_TASK(task.guild_id, task.id));
	} catch (error) {
		logger.error('Failed to delete to-do', error);
		Tasks.upsert(task);
	}
}

export function setArchived(task: Task, archived: boolean): Promise<void> {
	return updateTask(task, {archived});
}

/** Archives every to-do in one board column, or the whole board when no column is given. */
export async function archiveColumn(guildId: string, status?: TaskStatus): Promise<number> {
	try {
		const response = await http.post<{archived: number}>(Endpoints.GUILD_TASKS_ARCHIVE(guildId), {
			body: status ? {status} : {},
		});
		await loadGuildTasks(guildId, {force: true});
		return response.body?.archived ?? 0;
	} catch (error) {
		logger.error('Failed to archive to-dos', error);
		return 0;
	}
}

export function toggleDone(task: Task): Promise<void> {
	return updateTask(task, {status: task.status === 'done' ? 'todo' : 'done'});
}

/** Turns a chat message into a short to-do title. */
export function titleFromMessage(content: string): string {
	const firstLine = content.split('\n').find((line) => line.trim().length > 0) ?? '';
	const clean = firstLine
		.replace(/<[@#&!]?\d+>/g, '')
		.replace(/\s+/g, ' ')
		.trim();
	if (!clean) return 'Follow up';
	return clean.length > 140 ? `${clean.slice(0, 139)}…` : clean;
}

export async function makeTodoFromMessage(
	guildId: string,
	channelId: string,
	messageId: string,
	content: string,
): Promise<void> {
	await loadGuildTasks(guildId);
	const existing = Tasks.getTaskForMessage(messageId);
	if (existing) {
		ToastCommands.success(i18n._(TODO_ADDED_DESCRIPTOR));
		return;
	}
	const task = await createTask(guildId, {
		title: titleFromMessage(content),
		channel_id: channelId,
		message_id: messageId,
	});
	if (task) ToastCommands.success(i18n._(TODO_ADDED_DESCRIPTOR));
	else ToastCommands.error(i18n._(TODO_FAILED_DESCRIPTOR));
}
